import React, { useState } from 'react';
import { DollarSign, Plus, FileText, Trash2, Receipt, Download, Loader2 } from 'lucide-react';
import { generatePaymentInvoiceApi } from '../../../services/paymentService';
import { downloadBlob, base64ToBlob } from '../../../utils/consultationUtils';

/**
 * PaymentDetailsTable - Reusable component to display payment details with services and payment history
 * 
 * @param {Object} currentPayment - Payment object with services, amounts, and payment history
 * @param {Function} formatCurrency - Function to format currency values
 * @param {Function} formatDate - Function to format date values
 * @param {Function} getStatusBadge - Function to render status badge
 * @param {Boolean} showActions - Whether to show action buttons (default: true)
 * @param {Function} onAddPayment - Handler for adding payment
 * @param {Function} onAddService - Handler for adding service
 * @param {Function} onEditService - Handler for editing service
 * @param {Function} onRemoveService - Handler for removing service
 * @param {Function} onEditPaymentHistory - Handler for editing payment history
 * @param {Function} onDeletePaymentHistory - Handler for deleting payment history
 * @param {Boolean} isLastAppointment - Whether this is the last appointment
 * @param {Boolean} isAppointmentPast - Whether the appointment date has passed
 */
const PaymentDetailsTable = ({
  currentPayment,
  formatCurrency,
  formatDate,
  showActions = true,
  onAddPayment,
  onAddService,
  onEditService,
  onRemoveService,
  onEditPaymentHistory,
  onDeletePaymentHistory,
  isLastAppointment = true,
  isAppointmentPast = false
}) => {
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);



  /**
   * Handles PDF invoice generation
   */
  const handleGeneratePDF = async () => {
    if (!currentPayment?.id) {
      console.error('Payment ID is required');
      return;
    }

    setIsGeneratingPDF(true);

    try {
      // Get doctor ID from localStorage
      const doctolikInfo = JSON.parse(localStorage.getItem('doctolikLoginInfo'));
      const doctorId = doctolikInfo?.users?.type === 'Doctor' ? doctolikInfo?.users?.id : doctolikInfo?.users?.parent_id;

      if (!doctorId) {
        throw new Error('Doctor ID not found');
      }

      // Call API to generate invoice
      const response = await generatePaymentInvoiceApi(doctorId, currentPayment.id);

      // Handle response formats (support multiple server shapes):
      // 1) inline base64 file: response.data.invoice.file
      // 2) signed URL: response.data.invoice.url
      // 3) some endpoints wrap payload under response.data.data.invoice
      const invoice = response?.data?.invoice || response?.data?.data?.invoice || response?.invoice;
      if (invoice?.file) {
        const blob = base64ToBlob(invoice.file);
        const filename = invoice.filename || `Facture_${currentPayment.appointmentId}_${new Date().toISOString().split('T')[0]}.pdf`;
        downloadBlob(blob, filename);
        console.log('Invoice generated successfully (inline file)');
      } else if (invoice?.url) {
        // Fetch the signed URL and download the PDF blob
        const fetchRes = await fetch(invoice.url, { method: 'GET' });
        if (!fetchRes.ok) {
          throw new Error('Failed to download invoice from signed URL');
        }
        const blob = await fetchRes.blob();
        const filename = invoice.filename || `Facture_${currentPayment.appointmentId}_${new Date().toISOString().split('T')[0]}.pdf`;
        downloadBlob(blob, filename);
        console.log('Invoice downloaded from signed URL');
      } else {
        // Include response for easier debugging
        console.error('Unexpected invoice response', response?.data || response);
        throw new Error('Invalid response format from server');
      }
    } catch (error) {
      console.error('Error generating invoice:', error);

      // Show error message (you can use a toast notification here)
      const errorMessage = error.response?.data?.message || error.message || 'Failed to generate invoice';
      alert(`Erreur lors de la génération de la facture: ${errorMessage}`);
    } finally {
      setIsGeneratingPDF(false);
    }
  };
  if (!currentPayment) {
    return null;
  }

  const getPaymentTypeLabel = (type) => {
    const labels = {
      'normal': '💵',
      'debt_payment': '💰',
      'cancellation': '❌'
    };
    return labels[type] || '';
  };

  return (
    <div className="pmt-card">
      <div className="pmt-card-header">
        <div>
          <h2 className="pmt-card-title">Paiement Rendez-vous</h2>
          <p className="pmt-card-subtitle">
            {currentPayment.appointmentId} - {formatDate(currentPayment.appointmentDate)}
          </p>
        </div>
        <button
          onClick={handleGeneratePDF}
          className="pmt-btn pmt-btn-primary"
          disabled={!isLastAppointment || isGeneratingPDF}
          title={
            !isLastAppointment
              ? "Facture disponible uniquement pour le dernier rendez-vous"
              : isGeneratingPDF
                ? "Génération en cours..."
                : "Générer la facture PDF"
          }
          style={{
            opacity: (!isLastAppointment || isGeneratingPDF) ? 0.6 : 1,
            cursor: (!isLastAppointment || isGeneratingPDF) ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          {isGeneratingPDF ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Génération...
            </>
          ) : (
            <>
              <Receipt size={16} />
              Générer Facture
            </>
          )}
        </button>
      </div>

      {/* Services Table */}
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <h3 style={{ fontWeight: 600, color: '#374151', margin: 0 }}>Services</h3>
          {showActions && (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={onAddPayment}
                className="pmt-btn pmt-btn-success"
                disabled={!isLastAppointment}
                title={!isLastAppointment ? "Action disponible uniquement pour le dernier rendez-vous" : ""}
                style={{ opacity: !isLastAppointment ? 0.4 : 1, cursor: !isLastAppointment ? 'not-allowed' : 'pointer' }}
              >
                <DollarSign size={16} />
                Ajouter Paiement
              </button>
              <button
                onClick={onAddService}
                className="pmt-btn pmt-btn-primary"
                disabled={!isLastAppointment || isAppointmentPast}
                title={!isLastAppointment ? "Action disponible uniquement pour le dernier rendez-vous" : (isAppointmentPast ? "Impossible d'ajouter des services après la date du rendez-vous" : "")}
                style={{ opacity: (!isLastAppointment || isAppointmentPast) ? 0.4 : 1, cursor: (!isLastAppointment || isAppointmentPast) ? 'not-allowed' : 'pointer' }}
              >
                <Plus size={16} />
                Ajouter Service
              </button>
            </div>
          )}
        </div>

        <div className="pmt-table-container">
          <table className="pmt-table">
            <thead>
              <tr>
                <th className="text-left">Service</th>
                <th className="text-center">Quantité</th>
                <th className="text-right">Total</th>
                {showActions && <th className="text-center">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {currentPayment.services.map(service => (
                <tr key={service.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span>{service.name}</span>
                      {service.is_custom && (
                        <span
                          style={{
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            padding: '2px 8px',
                            borderRadius: '12px',
                            backgroundColor: '#dbeafe',
                            color: '#1e40af',
                            border: '1px solid #93c5fd'
                          }}
                          title="Service personnalisé ajouté par le médecin"
                        >
                          ✨ Personnalisé
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="text-center">{service.quantity}</td>
                  <td className="text-right font-semibold">{formatCurrency(service.total)}</td>
                  {showActions && (
                    <td className="text-center">
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                        <button
                          onClick={() => onEditService(service)}
                          className="pmt-btn-icon"
                          title={!isLastAppointment ? "Action disponible uniquement pour le dernier rendez-vous" : (isAppointmentPast ? "Impossible de modifier après la date du rendez-vous" : "Modifier")}
                          style={{ color: '#3b82f6', opacity: (!isLastAppointment || isAppointmentPast) ? 0.4 : 1, cursor: (!isLastAppointment || isAppointmentPast) ? 'not-allowed' : 'pointer' }}
                          disabled={!isLastAppointment || isAppointmentPast}
                        >
                          <FileText size={16} />
                        </button>
                        <button
                          onClick={() => onRemoveService(service.id)}
                          className="pmt-btn-icon"
                          title={!isLastAppointment ? "Action disponible uniquement pour le dernier rendez-vous" : (isAppointmentPast ? "Impossible de supprimer après la date du rendez-vous" : "Supprimer")}
                          style={{ opacity: (!isLastAppointment || isAppointmentPast) ? 0.4 : 1, cursor: (!isLastAppointment || isAppointmentPast) ? 'not-allowed' : 'pointer' }}
                          disabled={!isLastAppointment || isAppointmentPast}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray">
                <td colSpan={showActions ? "2" : "2"} className="text-right">Total à payer:</td>
                <td className="text-right text-lg">{formatCurrency(currentPayment.totalAmount)}</td>
                {showActions && <td></td>}
              </tr>
              <tr className="bg-green">
                <td colSpan={showActions ? "2" : "2"} className="text-right text-green">Montant Payé:</td>
                <td className="text-right text-green font-semibold">{formatCurrency(currentPayment.paidAmount)}</td>
                {showActions && <td></td>}
              </tr>
              {currentPayment.paidAmount > currentPayment.totalAmount && (
                <tr style={{ backgroundColor: '#d1fae5' }}>
                  <td colSpan={showActions ? "2" : "2"} className="text-right" style={{ color: '#065f46', fontWeight: 600 }}>Surplus (Crédit):</td>
                  <td className="text-right" style={{ color: '#065f46', fontWeight: 700, fontSize: '1rem' }}>
                    +{formatCurrency(currentPayment.paidAmount - currentPayment.totalAmount)}
                  </td>
                  {showActions && <td></td>}
                </tr>
              )}
              <tr className="bg-red">
                <td colSpan={showActions ? "2" : "2"} className="text-right text-red">Dette de la consultation:</td>
                <td className="text-right text-red text-lg">{formatCurrency(Math.max(0, currentPayment.debt))}</td>
                {showActions && <td></td>}
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Payment History */}
      {currentPayment.paymentHistory && currentPayment.paymentHistory.length > 0 && (
        <div style={{ marginTop: '1.5rem' }}>
          <h3 style={{ fontWeight: 600, color: '#374151', marginBottom: '0.75rem' }}>Historique des Paiements</h3>
          <div>
            {currentPayment.paymentHistory.map(payment => (
              <div key={payment.id} className="pmt-history-item">
                <div className="pmt-history-content" style={{ flex: '1', minWidth: '0' }}>
                  <p className="pmt-history-amount">{formatCurrency(payment.amount)}</p>
                  <p className="pmt-history-method">
                    {payment.paymentType && <span style={{ marginRight: '0.5rem', fontSize: '0.875rem' }}>{getPaymentTypeLabel(payment.paymentType)}</span>}
                    {payment.method} - {payment.note}
                  </p>
                </div>
                <div className="pmt-history-date" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', minWidth: '150px' }}>
                  <p style={{ margin: 0, marginBottom: '0.25rem' }}>{formatDate(payment.date)}</p>
                  <p className="pmt-history-recorded" style={{ margin: 0 }}>par {payment.recordedBy}</p>
                </div>
                {showActions && (
                  <div style={{ display: 'flex', gap: '0.5rem', marginLeft: '0.5rem' }}>
                    <button
                      onClick={() => onEditPaymentHistory(payment)}
                      className="pmt-btn-icon"
                      title={!isLastAppointment ? "Action disponible uniquement pour le dernier rendez-vous" : (isAppointmentPast ? "Impossible de modifier l'historique après la date du rendez-vous" : "Modifier")}
                      style={{ color: '#3b82f6', opacity: (!isLastAppointment || isAppointmentPast) ? 0.4 : 1, cursor: (!isLastAppointment || isAppointmentPast) ? 'not-allowed' : 'pointer' }}
                      disabled={!isLastAppointment || isAppointmentPast}
                    >
                      <FileText size={16} />
                    </button>
                    <button
                      onClick={() => onDeletePaymentHistory(payment)}
                      className="pmt-btn-icon"
                      title={!isLastAppointment ? "Action disponible uniquement pour le dernier rendez-vous" : (isAppointmentPast ? "Impossible de supprimer l'historique après la date du rendez-vous" : "Supprimer")}
                      style={{ color: '#dc2626', opacity: (!isLastAppointment || isAppointmentPast) ? 0.4 : 1, cursor: (!isLastAppointment || isAppointmentPast) ? 'not-allowed' : 'pointer' }}
                      disabled={!isLastAppointment || isAppointmentPast}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Notes */}
      {currentPayment.notes && (
        <div className="pmt-note">
          <p>
            <span className="pmt-note-label">Note:</span> {currentPayment.notes}
          </p>
        </div>
      )}
    </div>
  );
};

export default PaymentDetailsTable;
